import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  IconMap2,
  IconRepeat,
  IconTag,
  IconUsers,
  IconSpeakerphone,
  IconCamera,
  IconTrendingUp,
  IconHeart,
  IconChartBar,
  IconChecklist,
  IconSearch,
  IconTargetArrow,
  IconPrinter,
} from '@tabler/icons-react';
import { TeeSectionHeader } from '@/components/ui/tee-section-header';
import { TeeInput } from '@/components/ui/tee-input';
import { TeeLoader } from '@/components/ui/tee-loader';
import { TeeButton } from '@/components/ui/tee-button';
import { useOwnerPlaybook } from '@/hooks/useOwnerPlaybook';
import { usePlaybookSearch } from '@/hooks/useSearchScores';
import { useLocationPlans, pickFoundingPlans } from '@/hooks/useFoundingPricing';
import { useGamePlanLocation } from '@/hooks/useGamePlanLocation';
import { PlaybookChapter, PlaybookFragment } from '@/types/gamePlan';
import { BillingCycle, CommitmentType, Plan } from '@/types/plan';
import { LocationSwitcher } from '@/components/organisms/LocationSwitcher/LocationSwitcher';
import {
  SectionBlock,
  SectionNav,
  fragmentDomId,
  printAssetDomId,
  sectionDomId,
  type FoundingPriceCard,
  type FoundingPricingOverride,
} from './FragmentRenderer';
import { printChecklist } from './printChecklist';
import { printFundraiserNight } from './printFundraiser';
import { printKpis } from './printKpis';
import {
  printTimeline,
  printNonNegotiables,
  printSocialCalendar,
  printPersonaCards,
  printPitch,
  printRunOfShow,
} from './printAssets';

// The prototype's chapter icon keys → brand icon components.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CHAPTER_ICONS: Record<string, React.ComponentType<any>> = {
  map: IconMap2,
  repeat: IconRepeat,
  tag: IconTag,
  users: IconUsers,
  mega: IconSpeakerphone,
  camera: IconCamera,
  growth: IconTrendingUp,
  heart: IconHeart,
  chart: IconChartBar,
  check: IconChecklist,
  printer: IconPrinter,
};

// Synthetic, UI-only chapter — a home for the printable/office assets. Not
// content-driven (no fragments); rendered as a custom asset grid.
const PRINT_KIT: PlaybookChapter = {
  id: 'print-kit',
  title: 'Print Kit',
  icon: 'printer',
  sections: [],
};

export default function OwnerPlaybookPage() {
  const { t } = useTranslation();
  const { data: chapters, isLoading, isError } = useOwnerPlaybook();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  // Founding Member Offer pricing is driven by the location's real plans
  // ("Performance" monthly / "Performance Annual" yearly), not a static
  // placeholder. The location comes from the SHARED Game Plan selection
  // (useGamePlanLocation) — the same one Roadmap / This Week / Goal Board use —
  // so opening the Playbook while working on Central Houston shows Central
  // Houston's pricing. Keeping a private default here was the bug: it reset to
  // Centerville on every mount regardless of where the owner actually was.
  const {
    locations: switcherLocations,
    locationId: selectedLocationId,
    setLocationId: setSelectedLocationId,
    currentLocation,
  } = useGamePlanLocation();
  const { data: locationPlans } = useLocationPlans(selectedLocationId);
  const selectedLocationName = currentLocation?.name ?? '';

  const foundingPricing = useMemo<FoundingPricingOverride | undefined>(() => {
    const { monthly, annual } = pickFoundingPlans(locationPlans);
    if (!monthly && !annual) return undefined;

    const money = (amount: number, currency?: string) =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: (currency || 'USD').toUpperCase(),
        maximumFractionDigits: 0,
      }).format(amount ?? 0);
    const periodOf = (plan: Plan) => (plan.billingCycle === BillingCycle.YEARLY ? 'year' : 'month');
    const subOf = (plan: Plan) => {
      const term =
        plan.commitmentType === CommitmentType.ANNUAL
          ? `${plan.paymentTerm || 1}-year contract`
          : `${plan.paymentTerm || 1}-month term`;
      const billed = plan.billingCycle === BillingCycle.YEARLY ? 'billed annually' : 'billed monthly';
      return `${term}, ${billed}`;
    };

    const cards: FoundingPriceCard[] = [];
    if (monthly)
      cards.push({ label: monthly.name, amount: money(monthly.price, monthly.currency), period: periodOf(monthly), sub: subOf(monthly) });
    if (annual)
      cards.push({ label: annual.name, amount: money(annual.price, annual.currency), period: periodOf(annual), sub: subOf(annual) });

    const multi = (switcherLocations?.length ?? 0) > 1;
    const header = (
      <div className="flex items-center gap-2 text-[13px] text-[var(--text-content-sub-title)]">
        <span>{t('gamePlan.pricingFor', 'Pricing for')}</span>
        {multi && selectedLocationId ? (
          <LocationSwitcher
            currentLocationId={selectedLocationId}
            currentLocationName={selectedLocationName}
            onSelect={setSelectedLocationId}
          />
        ) : (
          <span className="font-semibold text-[var(--text-content-title)]">{selectedLocationName}</span>
        )}
      </div>
    );

    return {
      cards,
      header,
      note: t(
        'gamePlan.pricingLiveNote',
        "Live pricing from this location's Founding Membership plans.",
      ),
    };
  }, [locationPlans, switcherLocations, selectedLocationId, selectedLocationName, t]);

  // Rail = the content chapters from the API + the synthetic Print Kit at the end.
  const railChapters = useMemo<PlaybookChapter[]>(
    () => (chapters?.length ? [...chapters, PRINT_KIT] : []),
    [chapters],
  );

  const chapterParam = searchParams.get('chapter');
  const activeChapter: PlaybookChapter | undefined = useMemo(() => {
    if (!railChapters.length) return undefined;
    return railChapters.find((c) => c.id === chapterParam) ?? railChapters[0];
  }, [railChapters, chapterParam]);

  function goTo(chapterId: string, domId?: string) {
    setQuery('');
    navigate({
      pathname: location.pathname,
      search: `?chapter=${chapterId}`,
      hash: domId ? `#${domId}` : '',
    });
  }

  useEffect(() => {
    if (!location.hash || query) return;
    const id = location.hash.slice(1);
    const el = document.getElementById(id);
    if (el) requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }, [location.hash, activeChapter?.id, chapters, query]);

  // Flat index of every fragment with its chapter, keyed by id.
  const fragmentIndex = useMemo(() => {
    const index = new Map<
      string,
      { fragment: PlaybookFragment; chapterId: string; chapterTitle: string }
    >();
    chapters?.forEach((c) =>
      c.sections.forEach((s) =>
        s.fragments.forEach((f) =>
          index.set(f.id, { fragment: f, chapterId: c.id, chapterTitle: c.title }),
        ),
      ),
    );
    return index;
  }, [chapters]);

  // The server returns the final ranking (semantic + lexical blend). The UI
  // renders that order verbatim — no client-side re-ranking — so what users see
  // is exactly what the eval measures. While the request is in flight we show a
  // quick local lexical ordering so results feel instant.
  const { orderedIds } = usePlaybookSearch(query);
  const rankedResults = useMemo(() => {
    const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length === 0 || !chapters) return null;

    if (orderedIds) {
      return orderedIds
        .map((id) => fragmentIndex.get(id))
        .filter((e): e is NonNullable<typeof e> => Boolean(e))
        .slice(0, 20);
    }

    // Local fallback (loading / error): token-AND lexical ordering.
    const lexical = (f: PlaybookFragment): number => {
      const strong = `${f.title} ${f.label} ${f.tags.join(' ')}`.toLowerCase();
      const body = f.body.toLowerCase();
      let hit = 0;
      for (const tok of tokens) {
        if (strong.includes(tok)) hit += 1;
        else if (body.includes(tok)) hit += 0.4;
      }
      return hit / tokens.length;
    };
    return [...fragmentIndex.values()]
      .map((e) => ({ ...e, score: lexical(e.fragment) }))
      .filter((e) => e.score >= 0.1)
      .sort((a, b) => b.score - a.score)
      .slice(0, 20);
  }, [chapters, query, orderedIds, fragmentIndex]);

  // Search lives in the header's right slot (reclaims the empty space there and
  // removes a separate full-width row from the body).
  const searchInput = (
    <div className="w-full md:w-[340px]">
      <TeeInput
        inputSize="m"
        placeholder={t('gamePlan.searchPlaceholder', 'Search the playbook…')}
        leadingIcon={<IconSearch size={18} />}
        value={query}
        onChange={(e) => setQuery((e.target as HTMLInputElement).value)}
      />
    </div>
  );

  return (
    <div className="section_container">
      <TeeSectionHeader
        title={t('gamePlan.playbookTitle', 'Owner Playbook')}
        description={t('gamePlan.playbookSubtitle', 'Sales Strategy & Pre-Opening Playbook')}
        actionNode={searchInput}
      />

      <div className="section_body">
        {isLoading && <TeeLoader text={t('gamePlan.loadingPlaybook', 'Loading playbook…')} />}

        {isError && (
          <p className="text-[14px] text-[var(--text-badge-red)]">
            {t('gamePlan.playbookError', 'Could not load the playbook. Please try again.')}
          </p>
        )}

        {!isLoading && !isError && chapters && (
          <>
            <div className="grid grid-cols-1 gap-8 md:grid-cols-[260px_1fr]">
              {/* Chapter rail */}
              <nav className="flex flex-col gap-1.5 md:sticky md:top-4 md:self-start">
                {railChapters.map((chapter, i) => {
                  const Icon = CHAPTER_ICONS[chapter.icon] ?? IconTargetArrow;
                  const isActive = !query && chapter.id === activeChapter?.id;
                  return (
                    <button
                      key={chapter.id}
                      type="button"
                      onClick={() => goTo(chapter.id)}
                      className={
                        isActive
                          ? 'flex items-center gap-3 rounded-[8px] border border-[var(--border-active)] bg-[rgba(12,61,45,0.08)] px-3 py-2.5 text-left transition-colors'
                          : 'flex items-center gap-3 rounded-[8px] border border-transparent px-3 py-2.5 text-left transition-colors hover:bg-[var(--background-card-hover)]'
                      }
                    >
                      <span
                        className={
                          isActive
                            ? 'flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--color-forest)] text-[var(--text-on-forest)]'
                            : 'flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--color-gold-subtle)] text-[var(--color-text-gold)]'
                        }
                      >
                        <Icon size={16} />
                      </span>
                      <span className="flex flex-col">
                        <span className="text-[11px] font-semibold uppercase tracking-widest text-[var(--text-content-sub-title)]">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <span className="text-[14px] font-medium text-[var(--text-content-title)]">
                          {chapter.title}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </nav>

              {/* Reader */}
              <div className="flex flex-col gap-10">
                {rankedResults ? (
                  rankedResults.length === 0 ? (
                    <p className="text-[14px] text-[var(--text-content-sub-title)]">
                      {t('gamePlan.noResults', 'No matches found.')}
                    </p>
                  ) : (
                    <div className="flex max-w-[72ch] flex-col gap-2">
                      {rankedResults.map(({ fragment: f, chapterId, chapterTitle }) => (
                        // One flat, most-relevant-first list; chapter shown as a
                        // label rather than a grouping.
                        <button
                          key={f.id}
                          type="button"
                          onClick={() => goTo(chapterId, fragmentDomId(f))}
                          className="rounded-[10px] border border-[var(--border-default)] bg-[var(--background-card)] p-4 text-left transition-colors hover:bg-[var(--background-card-hover)]"
                        >
                          <span className="text-[11px] font-semibold uppercase tracking-widest text-[var(--color-text-gold)]">
                            {chapterTitle}
                          </span>
                          <span className="mt-0.5 block text-[15px] font-semibold text-[var(--text-content-title)]">
                            {f.title}
                          </span>
                          <span className="mt-1 line-clamp-2 block text-[13px] text-[var(--text-content-sub-title)]">
                            {f.body}
                          </span>
                        </button>
                      ))}
                    </div>
                  )
                ) : activeChapter?.id === 'print-kit' ? (
                  <PrintKitView chapters={chapters} />
                ) : (
                  <>
                    <div className="flex flex-col gap-4">
                      <h2 className="text-[24px] font-bold text-[var(--text-content-title)]">
                        {activeChapter?.title}
                      </h2>
                      {activeChapter && (
                        <SectionNav
                          titles={activeChapter.sections.map((s) => s.title)}
                          onJump={(title) =>
                            document
                              .getElementById(sectionDomId(title))
                              ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                          }
                        />
                      )}
                    </div>
                    {activeChapter?.sections.map((section) => (
                      <SectionBlock
                        key={section.title}
                        chapterId={activeChapter.id}
                        section={section}
                        foundingPricing={foundingPricing}
                      />
                    ))}
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

interface PrintAsset {
  /** Stable key — also the deep-link anchor other chapters point at. */
  key: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon?: React.ComponentType<any>;
  /** A bespoke artwork file instead of a glyph (public/ path). */
  iconSrc?: string;
  title: string;
  desc: string;
  onPrint: () => void;
}


/** The Print Kit — branded, office-ready sheets owners print and work from. */
function PrintKitView({ chapters }: { chapters: PlaybookChapter[] }) {
  const { t } = useTranslation();
  const { hash } = useLocation();
  // Arriving from a chapter deep-link (e.g. Marketing > "Full event plan →")
  // should land the owner ON the tile, not just on the page.
  const highlighted = hash ? hash.slice(1) : '';
  const assets: PrintAsset[] = [
    {
      key: 'launch-checklist',
      icon: IconChecklist,
      title: 'Pre-Sale Launch Checklist',
      desc: 'The full launch checklist, a blank "Your To-Dos" section, and the 16-week member progression. Tick it off.',
      onPrint: () => printChecklist(chapters),
    },
    {
      key: 'targets-worksheet',
      icon: IconChartBar,
      title: 'Targets Worksheet',
      desc: 'A weekly scoreboard — set your own targets (our HQ numbers are hints) and record each week’s actuals.',
      onPrint: () => printKpis(chapters),
    },
    {
      key: 'sixteen-week-plan',
      icon: IconMap2,
      title: '16-Week Game Plan',
      desc: 'The phase-by-phase wall planner — the 0→50 member ramp with focus for each phase.',
      onPrint: () => printTimeline(chapters),
    },
    {
      key: 'non-negotiables',
      icon: IconRepeat,
      title: 'Weekly Non-Negotiables Tracker',
      desc: 'A 16-week tick grid — run every non-negotiable, every week, and check it off.',
      onPrint: () => printNonNegotiables(chapters),
    },
    {
      key: 'social-calendar',
      icon: IconCamera,
      title: 'Social Content Calendar',
      desc: '16 weeks of posts from signing to opening — check each one off as it goes live.',
      onPrint: () => printSocialCalendar(chapters),
    },
    {
      key: 'persona-battlecards',
      icon: IconUsers,
      title: 'Persona Battlecards',
      desc: 'Sales cheat-sheets — who each persona is, what to lead with, how to handle objections.',
      onPrint: () => printPersonaCards(chapters),
    },
    {
      key: 'founding-pitch',
      icon: IconTag,
      title: 'Founding Member Pitch',
      desc: 'The one-page pitch script with the founders pricing and what never to say.',
      onPrint: () => printPitch(chapters),
    },
    {
      key: 'run-of-show',
      icon: IconSpeakerphone,
      title: 'Grand Opening Run of Show',
      desc: 'The day-of operating plan — timeline, checklist, run of show, invite list and logistics.',
      onPrint: () => printRunOfShow(chapters),
    },
    {
      // Self-contained template (not built from playbook fragments) — the sheet
      // the owner hands the coach. Deep-linked from Marketing > Low-cost.
      key: 'fundraiser-night',
      iconSrc: '/game-plan/event-icon.png',
      title: t('gamePlan.printKitFundraiserTitle', 'High School Fundraiser Night'),
      desc: t(
        'gamePlan.printKitFundraiserDesc',
        'Run of show, games, sponsor levels, raffle and revenue plan, to share with the coach.',
      ),
      onPrint: () => printFundraiserNight(),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1.5">
        <h2 className="text-[24px] font-bold text-[var(--text-content-title)]">
          {t('gamePlan.printKitTitle', 'Print Kit')}
        </h2>
        <p className="max-w-[72ch] text-[14px] leading-relaxed text-[var(--text-content-sub-title)]">
          {t(
            'gamePlan.printKitSubtitle',
            'Branded, office-ready sheets to print and work from — check off, fill in, pin up.',
          )}
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {assets.map((a) => {
          const domId = printAssetDomId(a.key);
          const isTarget = highlighted === domId;
          return (
          <div
            key={a.key}
            id={domId}
            className={
              'scroll-mt-4 flex flex-col gap-3 rounded-[10px] border bg-[var(--background-card)] p-5 transition-colors ' +
              (isTarget
                ? 'border-[var(--border-active)] shadow-[0_0_0_1px_var(--color-forest)]'
                : 'border-[var(--border-default)]')
            }
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-gold-subtle)] text-[var(--color-text-gold)]">
              {a.iconSrc ? (
                <img src={a.iconSrc} alt="" aria-hidden className="h-5 w-5 object-contain" />
              ) : (
                a.icon && <a.icon size={20} />
              )}
            </span>
            <h3 className="text-[16px] font-semibold text-[var(--text-content-title)]">{a.title}</h3>
            <p className="flex-1 text-[14px] leading-relaxed text-[var(--text-content-sub-title)]">
              {a.desc}
            </p>
            <div>
              <TeeButton
                variant="secondary"
                size="s"
                leftIcon={<IconPrinter size={16} />}
                text={t('gamePlan.printDownload', 'Print / Download')}
                onClick={a.onPrint}
              />
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}
